import type {
  BaseResponse,
  Event,
  FetchErrorResponse,
  Paging,
  PersonalMessage,
  PersonalMessageParams,
} from "@anggunpraya-templates/types";
import type { DeepPartial } from "@nuxt/ui/runtime/types/utils.js";
import type { FetchError } from "ofetch";
import { PAGING } from "@anggunpraya-templates/base/app/constant";
import { useDebounceFn, useInfiniteScroll } from "@vueuse/core";
import { PREVIEW_WISHES_DATA } from "../data/preview-wishes";

interface UseWishesFeedOptions {
  previewWishes?: BaseResponse<DeepPartial<PersonalMessage>, true>;
}

const defaultValue = {
  data: [],
  success: false,
  error: null,
  paging: null,
};

const defaultQueryParams = {
  keyword: "",
  order: "-created_at",
};

export async function useWishesFeed(options: UseWishesFeedOptions = {}) {
  const { $api } = useNuxtApp();
  const { flavor } = useRuntimeConfig().public;
  const { data: eventDetail } = useNuxtData<BaseResponse<Event>>("event-detail");
  const { isEventPreview } = useInvitationContext();
  const { coupleName } = useOpeningCard();

  const listRef = useTemplateRef<HTMLElement>("listRef");

  const isInitialLoad = ref(true);
  const data = ref<PersonalMessage[]>([]);
  const queryParams = ref<Pick<PersonalMessageParams, "keyword" | "order">>({
    ...defaultQueryParams,
  });

  const paging = ref<Paging>({
    ...PAGING,
  });

  const query = computed(() => ({
    ...queryParams.value,
    page: paging.value.page,
    limit: paging.value.limit,
  }));

  const wishesData = computed(() => {
    if (flavor === "event") {
      return [
        {
          personal_message_id: "anggun-praya-welcome-message",
          guest_name: "Anggun Praya",
          message: `Selamat menikah ${coupleName.value}! Semoga hari bahagia ini menjadi awal dari perjalanan yang penuh cinta, kebahagiaan, dan keberkahan bagi pasangan pengantin. Semoga setiap momen yang kalian lalui bersama dipenuhi dengan tawa, kebahagiaan, dan kenangan indah yang tak terlupakan. Selamat menempuh hidup baru🕊️`,
          status: "attending" as PersonalMessage["status"],
          created_at: new Date().toISOString(),
        },
        ...data.value,
      ];
    }

    return data.value;
  });

  const { status, error, refresh: refreshPersonalMessage } = await useAsyncData<BaseResponse<PersonalMessage, true>, FetchError<FetchErrorResponse>>(
    "personal-message",
    async (_nuxtApp, { signal }) => {
      if (flavor === "preview") {
        return await simulatePreviewFlavor();
      }

      return $api(`/event/${eventDetail.value?.data.event_id}/personal-message`, {
        signal,
        params: query.value,
        onResponse({ response }) {
          const { ok, _data } = response;

          if (ok && _data) {
            paging.value = {
              ...paging.value,
              ..._data.paging,
            };

            data.value = [
              ...data.value,
              ...(_data.data ?? []),
            ];
          }

          if (isInitialLoad.value) {
            isInitialLoad.value = false;
          }
        },
      });
    },
    {
      server: false,
      default: () => ({
        ...defaultValue,
      }),
    },
  );

  if (error.value) {
    showError({
      data: {
        ...error.value.data?.error,
      },
    });
  }

  const { reset, isLoading } = useInfiniteScroll(
    listRef,
    loadMore,
    {
      distance: 10,
      canLoadMore: () => {
        if (status.value === "success" && !isLoading.value && paging.value.next && flavor !== "portfolio" && !isEventPreview.value) {
          return true;
        }

        return false;
      },
    },
  );

  const debouncedRefresh = useDebounceFn(() => {
    refreshPersonalMessage();
  });

  const resetData = useDebounceFn(async () => {
    reset();

    data.value = [];
    paging.value = {
      ...PAGING,
    };

    await nextTick(() => {
      refreshPersonalMessage();

      if (listRef.value) {
        listRef.value.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    });
  }, 200);

  async function simulatePreviewFlavor() {
    const res = await Promise.resolve({
      ...(options.previewWishes ?? PREVIEW_WISHES_DATA),
    }) as BaseResponse<PersonalMessage, true>;

    const { paging: resPaging, data: resData } = res;

    paging.value = {
      ...paging.value,
      ...resPaging,
    };

    data.value = [
      ...data.value,
      ...(resData ?? []),
    ];

    return res;
  }

  async function loadMore() {
    paging.value.next = false;
    paging.value.page += 1;

    await debouncedRefresh();
  }

  return {
    data: wishesData,
    status,
    listRef,
    resetData,
  };
}
